import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Star, TrendingUp, MapPin, ArrowRight } from '../../lib/lucide-proxy';
import { Link } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const UserDashboard = () => {
  const [featuredBreweries, setFeaturedBreweries] = useState([]);
  const [newBreweries, setNewBreweries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch "Rising" (Featured)
        const { data: featured } = await supabase
          .from('breweries')
          .select('*, reviews(rating)')
          .eq('is_featured', true)
          .limit(3);

        // Fetch "New in Town"
        const { data: newInTown } = await supabase
          .from('breweries')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(3);

        setFeaturedBreweries(featured || []);
        setNewBreweries(newInTown || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <div className="flex justify-center p-12"><LoadingSpinner size="lg" /></div>;

  return (
    <>
      <Helmet>
        <title>Descobrir - Cerveja Delivery</title>
      </Helmet>

      <div className="space-y-10">
        <div>
          <h2 className="text-2xl font-bold text-dark-900">Bem-vindo ao Clube! 🍻</h2>
          <p className="text-dark-600">Descubra o melhor da cerveja artesanal na sua região.</p>
        </div>

        {/* Beer of the Week (Mocked for now) */}
        <div className="bg-gradient-to-r from-dark-900 to-dark-800 rounded-2xl p-8 shadow-medium relative overflow-hidden text-white">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500 rounded-full blur-3xl opacity-20 transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
            <img 
              src="https://images.unsplash.com/photo-1608270586620-248524c67de9?auto=format&fit=crop&w=300&q=80" 
              alt="Cerveja da Semana" 
              className="w-32 h-32 object-cover rounded-xl shadow-lg -rotate-6 border-2 border-primary-500/50" 
            />
            <div className="flex-1 text-center md:text-left">
              <div className="inline-block px-3 py-1 bg-primary-500 text-white text-xs font-bold rounded-full mb-3">
                SUGESTÃO DA SEMANA
              </div>
              <h3 className="text-2xl font-display font-bold mb-2">IPA Tropical Haze</h3>
              <p className="text-gray-300 mb-4 max-w-lg">
                Uma explosão de aromas frutados com notas de maracujá e manga. Perfeita para os dias quentes.
              </p>
              <Link to="/cervejarias" className="inline-flex items-center text-primary-400 hover:text-primary-300 font-bold transition-colors">
                Encontrar Cervejarias <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>

        {/* Rising Breweries */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-dark-900 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-primary-500" /> Em Ascensão
            </h3>
            <Link to="/cervejarias" className="text-sm text-primary-600 font-medium hover:underline">Ver todas</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredBreweries.map(brewery => (
              <Link key={brewery.id} to={`/loja/${brewery.slug}`} className="group block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all">
                <div className="h-32 overflow-hidden">
                  <img 
                    src={brewery.cover_image} 
                    alt={brewery.name} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                  />
                </div>
                <div className="p-4">
                  <h4 className="font-bold text-dark-900 mb-1 truncate">{brewery.name}</h4>
                  <div className="flex items-center text-sm text-dark-500 mb-3">
                    <MapPin className="w-3 h-3 mr-1" />
                    <span className="truncate">{brewery.address}</span>
                  </div>
                  <div className="flex items-center text-xs font-medium text-yellow-500">
                    <Star className="w-3 h-3 fill-current mr-1" /> 4.8 (120 avaliações)
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* New in Town */}
        <div>
          <h3 className="text-xl font-bold text-dark-900 mb-6">Novas na Cidade</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {newBreweries.map(brewery => (
              <Link key={brewery.id} to={`/loja/${brewery.slug}`} className="flex items-center p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:border-primary-200 transition-colors">
                <img src={brewery.logo} alt={brewery.name} className="w-16 h-16 rounded-lg object-cover mr-4" />
                <div>
                  <h4 className="font-bold text-dark-900 text-sm">{brewery.name}</h4>
                  <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full mt-1 inline-block">
                    Novo
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default UserDashboard;